import { createColumnBuilders, DataTableColumn } from '../../../shared/components/DataTable'
import { getAcronym } from '../../../shared/utils/acronym'
import { ActivityColumnId, ActivityDisplayNames, ActivityRow } from './ActivityTable.types'

type ActivityColumn = DataTableColumn<ActivityRow, ActivityColumnId>

const { text, date, count, yesNo } = createColumnBuilders<ActivityRow, ActivityColumnId>()

export const getActivityColumns = ({
    valueMessages,
    strategicImperatives,
    tpp,
    evidence,
}: ActivityDisplayNames): ActivityColumn[] => [
    text('title', 'Short Title', (row) => row.title, { width: 400 }),
    text('titleLong', 'Long Title', (row) => row.titleLong, { width: 400 }),
    text('source', 'Source System', (row) => row.source, { align: 'center' }),
    text('externalId', 'External ID', (row) => row.externalId),
    text('studyNumber', 'Study Number', (row) => row.studyNumber),
    { id: 'status', label: 'Status', width: 150 },
    text('activityTimeline', 'Activity Timeline', (row) => row.activityTimelineLabel, { align: 'center', width: 220 }),
    date('completionDate', 'Completion Date', (row) => row.completionDate),
    text('type', 'Type', (row) => row.type, { width: 175 }),
    text('category', 'Category', (row) => row.category, { width: 175 }),
    text('function', 'Function', (row) => row.function, { width: 220 }),
    text('ownerRoadmapName', 'Owned By', (row) => row.ownerRoadmapName ?? undefined),
    text('affiliateName', 'Affiliate', (row) => row.affiliateName),
    text('countries', 'Countries', (row) => row.countries?.join(', ')),
    yesNo('corporateOrRnDGoal', 'Corporate / R&D Goal', (row) => !!row.corporateOrRnDGoal),
    count('linkedValueMessageCount', `Linked ${getAcronym(valueMessages)}`, (row) => row.linkedValueMessageCount, {
        tooltip: `Linked ${valueMessages}`,
    }),
    count(
        'linkedStrategicImperativeCount',
        `Linked ${getAcronym(strategicImperatives)}`,
        (row) => row.linkedStrategicImperativeCount,
        { tooltip: `Linked ${strategicImperatives}` }
    ),
    count('linkedTppCellCount', `Linked ${getAcronym(tpp)} Cells`, (row) => row.linkedTppCellCount),
    count('linkedEvidenceCount', `Linked ${getAcronym(evidence)}`, (row) => row.linkedEvidenceCount, {
        tooltip: `Linked ${evidence}`,
    }),
    text('population', 'Population', (row) => row.population),
    text('sponsorType', 'Sponsor Type', (row) => row.sponsorType),
    text('studyOwner', 'Study Owner', (row) => row.studyOwner),
    text('providerName', 'Provider', (row) => row.providerName),
    yesNo('vendor', 'Vendor', (row) => !!row.vendor),
    yesNo('patientStudy', 'Patient Study', (row) => !!row.patientStudy),
    yesNo('funded', 'Funded', (row) => !!row.funded),
    { id: 'budget', label: 'Budget', align: 'center', getSortValue: (row) => row.budgetAmount },
    date('startDate', 'Start Date', (row) => row.startDateValue),
    date('endDate', 'End Date', (row) => row.endDateValue),
    date('plannedStartDate', 'Planned Start', (row) => row.plannedStartDateValue),
    date('plannedEndDate', 'Planned End', (row) => row.plannedEndDateValue),
    date('studyReportDate', 'Study Report Date', (row) => row.studyReportDateValue),
    date('resultsAvailableForExternalUseDate', 'Results Available', (row) => row.resultsAvailableDateValue),
    date('terminationDate', 'Termination Date', (row) => row.terminationDateValue),
    text('therapeuticArea', 'Therapeutic Area', (row) => row.therapeuticArea),
    text('indication', 'Indication', (row) => row.indication, { width: 250 }),
    text('compoundName', 'Compound', (row) => row.compoundName?.join(', '), { width: 160 }),
    text('genericName', 'Generic Name', (row) => row.genericName?.join(', ')),
    count('deliverableCount', 'Deliverables', (row) => row.deliverableCount),
    count('activityDependencyCount', 'Dependencies', (row) => row.activityDependencyCount),
    text('dataSource', 'Data Source', (row) => row.dataSource),
    date('lastSyncedAt', 'Last Synced', (row) => row.lastSyncedAtValue),
    date('createdAt', 'Created', (row) => row.createdAtValue),
    date('updatedAt', 'Updated', (row) => row.updatedAtValue),
]
